import * as React from "react";
import { cn } from "@/lib/utils";

interface DualScrollbarProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  enableKeyboardNavigation?: boolean;
  scrollStep?: number;
}

/**
 * Container with synchronized scrollbars at top AND bottom,
 * plus optional keyboard navigation (Left/Right arrow keys).
 */
const DualScrollbar = React.forwardRef<HTMLDivElement, DualScrollbarProps>(
  ({ className, children, enableKeyboardNavigation = true, scrollStep = 200, ...props }, ref) => {
    const topScrollRef = React.useRef<HTMLDivElement>(null);
    const bottomScrollRef = React.useRef<HTMLDivElement>(null);
    const contentRef = React.useRef<HTMLDivElement>(null);
    const [contentWidth, setContentWidth] = React.useState(0);
    const [isSyncing, setIsSyncing] = React.useState(false);

    // Measure content width for the top scrollbar dummy
    React.useEffect(() => {
      const updateWidth = () => {
        if (contentRef.current) {
          setContentWidth(contentRef.current.scrollWidth);
        }
      };
      
      updateWidth();
      
      // Use ResizeObserver for dynamic content
      const observer = new ResizeObserver(updateWidth);
      if (contentRef.current) {
        observer.observe(contentRef.current);
      }
      
      return () => observer.disconnect();
    }, [children]);

    // Sync scroll positions between top and bottom scrollbars
    const handleTopScroll = () => {
      if (isSyncing || !topScrollRef.current || !bottomScrollRef.current) return;
      setIsSyncing(true);
      bottomScrollRef.current.scrollLeft = topScrollRef.current.scrollLeft;
      requestAnimationFrame(() => setIsSyncing(false));
    };

    const handleBottomScroll = () => {
      if (isSyncing || !topScrollRef.current || !bottomScrollRef.current) return;
      setIsSyncing(true);
      topScrollRef.current.scrollLeft = bottomScrollRef.current.scrollLeft;
      requestAnimationFrame(() => setIsSyncing(false));
    };

    // Keyboard navigation
    const handleKeyDown = React.useCallback((e: KeyboardEvent) => {
      if (!enableKeyboardNavigation || !bottomScrollRef.current) return;
      
      // Only handle if the container or its children are focused
      const container = bottomScrollRef.current;
      if (!container.contains(document.activeElement) && document.activeElement !== container) {
        return;
      }

      if (e.key === "ArrowLeft") {
        e.preventDefault();
        container.scrollBy({ left: -scrollStep, behavior: "smooth" });
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        container.scrollBy({ left: scrollStep, behavior: "smooth" });
      }
    }, [enableKeyboardNavigation, scrollStep]);

    React.useEffect(() => {
      if (enableKeyboardNavigation) {
        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
      }
    }, [enableKeyboardNavigation, handleKeyDown]);

    return (
      <div ref={ref} className={cn("relative", className)} {...props}>
        {/* Top scrollbar */}
        <div
          ref={topScrollRef}
          onScroll={handleTopScroll}
          className="overflow-x-auto overflow-y-hidden h-4 border-b border-border bg-muted/30"
          style={{ scrollbarWidth: "thin" }}
        >
          <div style={{ width: contentWidth, height: 1 }} />
        </div>

        {/* Main scrollable content with bottom scrollbar */}
        <div
          ref={bottomScrollRef}
          onScroll={handleBottomScroll}
          tabIndex={0}
          className="overflow-x-auto focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
          style={{ scrollbarWidth: "thin" }}
        >
          <div ref={contentRef}>
            {children}
          </div>
        </div>

        {/* Keyboard hint */}
        {enableKeyboardNavigation && (
          <div className="hidden lg:flex items-center justify-center gap-2 text-xs text-muted-foreground py-2 border-t border-border bg-muted/20">
            <kbd className="px-2 py-0.5 rounded bg-muted border border-border font-mono">←</kbd>
            <span>/</span>
            <kbd className="px-2 py-0.5 rounded bg-muted border border-border font-mono">→</kbd>
            <span className="ml-1">zum Scrollen</span>
          </div>
        )}
      </div>
    );
  }
);

DualScrollbar.displayName = "DualScrollbar";

export { DualScrollbar };
